import { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import {
  pushData,
  pushUserData,
  pushFunnelEvent,
  pushEcommerceEvent,
} from '@app/shop/tracking';

import { UserQuery } from './__generated__/UserQuery';
import {
  CartQuery_magento2,
  CartQuery_magento2_cart,
} from '../context/__generated__/CartQuery';
import { convertToEuro } from '../helpers';

export const USER_QUERY = gql`
  query UserQuery {
    user {
      id
      isAuthenticated
      country
      upgrades
    }
  }
`;

function useGTMTracking({
  step,
  items,
  prices,
  storeConfig,
  cartId,
}: {
  step?: string;
  items?: CartQuery_magento2_cart['items'];
  prices?: CartQuery_magento2_cart['prices'];
  storeConfig?: CartQuery_magento2['storeConfig'];
  cartId?: string;
}): void {
  const { data } = useQuery<UserQuery>(USER_QUERY);
  const user = data?.user;
  const { isAuthenticated = false, country, upgrades } = user ?? {};

  const { language } = useParams<{ language: string }>();

  const { grandTotal, subtotalIncludingTax, subtotalExcludingTax } =
    prices || {};

  const products = items?.map((item) => ({
    productId: item?.product.sku,
    productName: item?.product.name,
    price: convertToEuro(
      Number(item?.prices?.priceIncludingTax.value) * Number(item?.quantity),
      item?.prices?.priceIncludingTax?.currency
    ),
    quantity: item?.quantity,
    category: '',
    subCategory: '',
  }));

  const includingTax = storeConfig?.taxSalesDisplaySubtotal === 'including';

  useEffect(() => {
    if (country) {
      if (!step || step === 'cart') {
        pushUserData({
          isAuthenticated,
          country,
          language,
          pageCategory: 'shop',
        });
      } else {
        pushData({
          lang: language,
          country,
        });
      }

      if (step === 'cart') {
        pushData({
          checkoutStartedTime: Date.now(),
        });
      }

      if (!step && upgrades?.length) {
        pushData({
          event: 'analyticsEvent',
          eventCategory: 'My Offers',
          eventAction: 'notification',
          eventLabel: upgrades.length,
          eventValue: upgrades,
        });
      }
    }
  }, [country, step]);

  const handleCommerceEvent = (step, products) => {
    pushEcommerceEvent('Checkout', {
      checkout: {
        actionField: {
          step,
          option: '',
        },
        products,
      },
    });
  };

  useEffect(() => {
    if (items) {
      if (step === 'cart') {
        const data = {
          cartItemCount: items.reduce((acc, item) => {
            if (item?.quantity) {
              acc = acc + item?.quantity;
            }

            return acc;
          }, 0),
          event: 'funnel',
          grandtotal: convertToEuro(grandTotal?.value, grandTotal?.currency),
          products,
          stcid: cartId,
          step,
          subtotal: convertToEuro(
            includingTax
              ? subtotalIncludingTax?.value
              : subtotalExcludingTax?.value,
            grandTotal?.currency
          ),
        };

        pushData({
          ...data,
          products: null,
        });
        pushData(data);
      } else if (step) {
        pushFunnelEvent(step, products);

        if (step === 'address') {
          handleCommerceEvent(1, products);
          pushFunnelEvent('checkout', products);
        }
      }
    }
  }, [items]);
}

export default useGTMTracking;
